/**
 * ConflictResolver Component
 *
 * Displays a table of booking conflicts allowing users to review and selectively
 * resolve conflicts before saving. Shows project hours, total hours, and delta
 * changes with visual indicators for overbooking.
 */

import React from "react";
import classNames from "classnames";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Icon } from "@/components/ui/display/Icon";
import { generateId } from "@/lib/utils";
import type { ConflictInfo } from "@/hooks/booking/useConflictResolution";
import styles from "./ConflictResolver.module.css";

export interface ConflictResolverProps {
  /**
   * Array of conflict information for each booking
   */
  conflicts: ConflictInfo[];

  /**
   * Set of indices that are omitted (will not be saved)
   */
  omittedIndices: Set<number>;

  /**
   * Total number of conflict dates
   */
  totalConflictDates: number;

  /**
   * Callback to toggle omission of a specific index
   */
  onToggleOmit: (index: number) => void;

  /**
   * Callback to mark all as save
   */
  onSaveAll: () => void;

  /**
   * Callback to omit all issues
   */
  onOmitAll: () => void;

  /**
   * Format hours for display
   */
  formatHours: (hours: number) => string;

  /**
   * Format date for display
   */
  formatDate: (date: Date) => string;

  /**
   * Hours in a standard workday (default 8)
   */
  hoursInDay?: number;

  /**
   * Optional CSS class
   */
  className?: string;

  /**
   * Test ID for testing
   */
  "data-testid"?: string;
}

/**
 * ConflictResolver displays booking conflicts in a table format
 */
export const ConflictResolver: React.FC<ConflictResolverProps> = ({
  conflicts,
  omittedIndices,
  totalConflictDates,
  onToggleOmit,
  onSaveAll,
  onOmitAll,
  formatHours,
  formatDate,
  hoursInDay = 8,
  className,
  "data-testid": testId,
}) => {
  // Filter to only show issues
  const issues = conflicts.filter((c) => c.isIssue);

  if (issues.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.title}>
            Booking kolliderer med {totalConflictDates} eksisterende booking
            {totalConflictDates === 1 ? "" : "er"}.
          </span>
          <span className={styles.subtitle}>
            Se gjennom og bekreft hvilke endringer som skal lagres.
          </span>
        </div>

        {/* Bulk actions - only show if more than 2 conflicts */}
        {totalConflictDates > 2 && (
          <div className={styles.bulkActions}>
            <ActionButton
              label="Marker alle"
              size="small"
              variant="highlight"
              onClick={onSaveAll}
              disabled={omittedIndices.size === 0}
              data-testid="conflict-save-all"
            />
            <ActionButton
              label="Marker ingen"
              size="small"
              variant="highlight"
              onClick={onOmitAll}
              disabled={omittedIndices.size === totalConflictDates}
              data-testid="conflict-omit-all"
            />
          </div>
        )}
      </div>

      {/* Conflict table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.colDate}>Dato</th>
              <th className={styles.colHours}>Prosjekt</th>
              <th className={styles.colHours}>Totalt</th>
              <th className={styles.colDelta}>Endring</th>
              <th className={styles.colCheckbox}></th>
            </tr>
          </thead>
          <tbody>
            {issues.map((conflict) => {
              const isOmitted = omittedIndices.has(conflict.index);
              const isProjectOverbooked =
                conflict.projectHoursFrom > hoursInDay;
              const isTotalFromOverbooked =
                conflict.totalHoursFrom > hoursInDay;
              const isTotalToOverbooked = conflict.totalHoursTo > hoursInDay;
              const isDeltaPositive = conflict.hoursDelta > 0;
              const isDeltaNegative = conflict.hoursDelta < 0;

              return (
                <tr
                  key={conflict.index}
                  className={classNames(styles.row, {
                    [styles.rowOmitted]: isOmitted,
                  })}
                  data-testid={`conflict-row-${conflict.index}`}
                >
                  {/* Date column */}
                  <td className={styles.colDate}>
                    <span className={styles.dateText}>
                      {formatDate(conflict.date)}
                    </span>
                  </td>

                  {/* Project hours column */}
                  <td className={styles.colHours}>
                    <div className={styles.hoursInner}>
                      <span
                        className={classNames(styles.hoursValue, styles.left, {
                          [styles.red]: isProjectOverbooked,
                        })}
                      >
                        {formatHours(conflict.projectHoursFrom)}
                      </span>
                      {conflict.projectHoursFrom !==
                        conflict.projectHoursTo && (
                        <>
                          <Icon
                            name="arrowRight"
                            size="sm"
                            className={styles.arrow}
                          />
                          <span
                            className={classNames(
                              styles.hoursValue,
                              styles.right,
                              {
                                [styles.strikethrough]: isOmitted,
                              },
                            )}
                          >
                            {formatHours(conflict.projectHoursTo)}
                          </span>
                        </>
                      )}
                    </div>
                  </td>

                  {/* Total hours column */}
                  <td className={styles.colHours}>
                    <div className={styles.hoursInner}>
                      <span
                        className={classNames(styles.hoursValue, styles.left, {
                          [styles.red]: isTotalFromOverbooked,
                        })}
                      >
                        {formatHours(conflict.totalHoursFrom)}
                      </span>
                      <Icon
                        name="arrowRight"
                        size="sm"
                        className={styles.arrow}
                      />
                      <span
                        className={classNames(styles.hoursValue, styles.right, {
                          [styles.strikethrough]: isOmitted,
                          [styles.red]: isTotalToOverbooked && !isOmitted,
                        })}
                      >
                        {formatHours(conflict.totalHoursTo)}
                      </span>
                    </div>
                  </td>

                  {/* Delta column */}
                  <td className={styles.colDelta}>
                    <span
                      className={classNames(styles.deltaValue, {
                        [styles.red]: isDeltaNegative && !isOmitted,
                        [styles.green]: isDeltaPositive && !isOmitted,
                        [styles.strikethrough]: isOmitted,
                      })}
                    >
                      {isDeltaPositive ? "+" : ""}
                      {formatHours(conflict.hoursDelta)}
                    </span>
                  </td>

                  {/* Checkbox column */}
                  <td className={styles.colCheckbox}>
                    <Checkbox
                      id={generateId(`conflict-checkbox-${conflict.index}`)}
                      checked={!isOmitted}
                      onChange={() => onToggleOmit(conflict.index)}
                      label=""
                      data-testid={`conflict-checkbox-${conflict.index}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ConflictResolver.displayName = "ConflictResolver";

export default ConflictResolver;
